/**
 * Checklists API endpoints
 */

import { apiClient } from './client';
import { z } from 'zod';
import { paginatedResponse } from './schemas';
import { parseResponse } from '@/lib/parseResponse';
import {
  bulkArchive,
  bulkUnarchive,
  bulkChangeOwner,
  bulkAddLabels,
  bulkRemoveLabels,
} from './bulkOperations';
import { getLabels, deleteLabel } from './labels';
import type {
  Checklist,
  ChecklistListParams,
  ChecklistListResponse,
  ChecklistCreatePayload,
  ChecklistUpdatePayload,
  ChecklistLabelCategory,
  ChecklistLabelCategoryWithLabels,
  ResolvePayload,
  Question,
} from '@/types';

// F9: PURE — returns a new object instead of mutating in place.
function withShareCompat(checklist: Checklist): Checklist {
  if (checklist.shares?.length && !checklist.share) {
    return { ...checklist, share: checklist.shares[0] };
  }
  return checklist;
}

// F9: schema for the checklist-list envelope.
const ChecklistListSchema = paginatedResponse(z.record(z.string(), z.unknown()));

/** The checklists list endpoint (list GET and the bulk select-all request_url). */
export const CHECKLISTS_LIST_PATH = '/api/checklists/';

/** The query params the list GET sends: ONE definition, shared with the bulk request_url. */
export function checklistListWireParams(params: ChecklistListParams = {}): Record<string, unknown> {
  const { labels, ...rest } = params;
  return {
    ...rest,
    ...(labels?.length && { labels: labels.join(',') }),
  };
}

export async function getChecklists(params: ChecklistListParams = {}): Promise<ChecklistListResponse> {
  const response = await apiClient.get<unknown>(CHECKLISTS_LIST_PATH, {
    params: checklistListWireParams(params),
  });
  // F9: boundary-validate via parseResponse.
  const parsed = parseResponse(ChecklistListSchema, response.data);
  const results = (parsed.results as unknown as Checklist[]).map(withShareCompat);
  return { ...parsed, results } as unknown as ChecklistListResponse;
}

// Get single checklist by ID
export async function getChecklist(id: number): Promise<Checklist> {
  const response = await apiClient.get<Checklist>(`/api/checklists/${id}/`);
  return withShareCompat(response.data);
}

// Create new checklist. Copy/revise ride in the body (ChecklistCreateInput:
// checklist_id/copy/revise, snake_cased by the client) — identical to
// createPacket. vingapi still accepts the same fields as query params for
// back-compat with older clients, but the body is canonical and wins per-field
// (vingapi #4177 / 8bc2ad69); sending both would just duplicate the payload.
export async function createChecklist(payload: ChecklistCreatePayload = {}): Promise<Checklist> {
  const response = await apiClient.post<Checklist>('/api/checklists/', payload);
  return withShareCompat(response.data);
}

// Update checklist
export async function updateChecklist(id: number, payload: ChecklistUpdatePayload): Promise<Checklist> {
  const response = await apiClient.put<Checklist>(`/api/checklists/${id}/`, payload);
  return withShareCompat(response.data);
}

// ---------------------------------------------------------------------------
// Question-scoped PATCH (vingapi#913) — replace a failed question image, edit
// text. The body accepts only the non-derived fields; the response is the full
// ChecklistDetail (capabilities included).
// ---------------------------------------------------------------------------

/**
 * Allowed body fields for `PATCH /api/checklists/{id}/questions/{qid}/`.
 * Every field is optional (absent != null). Derived/read-only fields are
 * rejected with 422.
 */
/** A choice row in a checklist question PATCH — id required on every choice. */
export interface ChecklistQuestionPatchChoice {
  id: number;
  text: string;
}

export interface ChecklistQuestionPatchBody {
  text?: string | null;
  choices?: ChecklistQuestionPatchChoice[] | null;
  image?: { key: string } | null;
}

export async function patchChecklistQuestion(
  checklistId: number,
  questionId: number,
  body: ChecklistQuestionPatchBody,
): Promise<Checklist> {
  const response = await apiClient.patch<Checklist>(
    `/api/checklists/${checklistId}/questions/${questionId}/`,
    body,
  );
  return withShareCompat(response.data);
}

// ---------------------------------------------------------------------------
// Title/summary update (plan v5) — PUT with exactly {title, summary}.
// A dedicated function so no call site can carry questions/status.
// ---------------------------------------------------------------------------

import type { TitleSummaryBody } from './packets';

export async function updateChecklistTitleSummary(
  id: number,
  body: TitleSummaryBody,
): Promise<Checklist> {
  const response = await apiClient.put<Checklist>(`/api/checklists/${id}/`, body);
  return withShareCompat(response.data);
}

// Delete checklist
export async function deleteChecklist(id: number): Promise<void> {
  await apiClient.delete(`/api/checklists/${id}/`);
}

// Publish checklist (update status to published)
export async function publishChecklist(id: number): Promise<Checklist> {
  return updateChecklist(id, { status: 'published' });
}

const CHECKLISTS_ENDPOINT = '/api/checklists/';

// Archive checklists (bulk)
export const archiveChecklists = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean
): Promise<void> =>
  bulkArchive(CHECKLISTS_ENDPOINT, { selectedIds, excludedIds, selectedAll });

// Unarchive checklists (bulk)
export const unarchiveChecklists = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean
): Promise<void> =>
  bulkUnarchive(CHECKLISTS_ENDPOINT, { selectedIds, excludedIds, selectedAll });

// Change owner (bulk)
export const changeChecklistOwner = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean,
  ownerId: string
): Promise<void> =>
  bulkChangeOwner(
    CHECKLISTS_ENDPOINT,
    { selectedIds, excludedIds, selectedAll },
    parseInt(ownerId, 10)
  );

// Add labels (bulk)
export const addChecklistLabels = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean,
  labels: string[]
): Promise<void> =>
  bulkAddLabels(CHECKLISTS_ENDPOINT, { selectedIds, excludedIds, selectedAll }, labels);

// Remove labels (bulk)
export const removeChecklistLabels = (
  selectedIds: number[],
  excludedIds: number[],
  selectedAll: boolean,
  labels: string[]
): Promise<void> =>
  bulkRemoveLabels(CHECKLISTS_ENDPOINT, { selectedIds, excludedIds, selectedAll }, labels)

// Get checklist labels (categorized) - uses shared labels utility
export const getChecklistLabels = (): Promise<ChecklistLabelCategoryWithLabels[]> =>
  getLabels('checklist').then((categories) =>
    categories.map((cat) => ({
      name: cat.name,
      labels: cat.labels.map((l) => ({ name: l.name, count: l.objectCount })),
    }))
  );

// Delete checklist label - uses shared labels utility
export const deleteChecklistLabel = (label: string): Promise<void> =>
  deleteLabel('checklist', label);

// Create label category
export async function createChecklistLabelCategory(
  category: string,
  labels: string[]
): Promise<ChecklistLabelCategory> {
  const response = await apiClient.post<ChecklistLabelCategory>(
    `/api/labels/categories/checklist/${encodeURIComponent(category)}/`,
    { labels }
  );
  return response.data;
}

// Delete label category
export async function deleteChecklistLabelCategory(category: string): Promise<void> {
  await apiClient.delete(`/api/labels/categories/checklist/${encodeURIComponent(category)}/`);
}

// Resolve submissions
export async function resolveSubmissions(payload: ResolvePayload): Promise<void> {
  await apiClient.post('/api/resolve/submissions/', payload);
}

// Resolve responses
export async function resolveResponses(payload: ResolvePayload): Promise<void> {
  await apiClient.post('/api/resolve/responses/', payload);
}

// Copy checklist
export async function copyChecklist(checklistId: number): Promise<Checklist> {
  return createChecklist({ checklistId, copy: true });
}

// Revise checklist (create new revision)
export async function reviseChecklist(
  checklistId: number,
): Promise<Checklist> {
  return createChecklist({
    checklistId,
    revise: true,
  });
}

// Question operations

// F10: atomic per-question endpoint — no read-then-write race.
export async function addQuestion(
  checklistId: number,
  questionData: Partial<Question>
): Promise<Checklist> {
  const response = await apiClient.post<Checklist>(
    `/api/checklists/${checklistId}/questions/`,
    questionData,
  );
  return response.data;
}

// F10: atomic per-question endpoint — no read-then-write race.
export async function updateQuestion(
  checklistId: number,
  questionId: number,
  questionData: Partial<Question>
): Promise<Checklist> {
  const response = await apiClient.put<Checklist>(
    `/api/checklists/${checklistId}/questions/${questionId}/`,
    questionData,
  );
  return response.data;
}

// F10: atomic per-question endpoint — no read-then-write race.
export async function deleteQuestion(
  checklistId: number,
  questionId: number
): Promise<Checklist> {
  const response = await apiClient.delete<Checklist>(
    `/api/checklists/${checklistId}/questions/${questionId}/`,
  );
  return response.data;
}

// Reorder questions in a checklist
export async function reorderQuestions(
  checklistId: number,
  questions: Question[]
): Promise<Checklist> {
  return updateChecklist(checklistId, { questions });
}

// Resolve a single response
export async function resolveResponse(responseId: number): Promise<void> {
  await apiClient.post('/api/resolve/responses/', {
    responseIds: [responseId],
  });
}

// Re-export unsent checklist invitations from invitations module (properly typed)
export { listUnsentChecklistInvitations as getUnsentChecklistInvitations } from './invitations';
